import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, ChevronDown, ChevronUp, ArrowRight,
  UserPlus, Network, ClipboardCheck, MessageCircle, Mail, Snowflake,
  History, FlaskConical, Radar, AlertTriangle, ArrowLeftRight, Route,
  Target, Wand2, Bell, Users, CheckSquare, Puzzle,
} from 'lucide-react';

const STAGES = [
  {
    id: 'crawl', badge: 'Stage 1', name: 'Crawl',
    promise: 'Capture everything, without typing',
    timeline: 'Weeks 1 to 2',
    framing: 'Your pipeline is not wrong because reps lie. It is wrong because nobody has time to type. Forsyt reads the calls, the inbox, Slack and WhatsApp, and rebuilds the record for them.',
    outcome: 'A CRM that is finally complete and AI-ready, and kept that way with zero rep admin.',
    workflows: [
      { icon: UserPlus, label: 'Contacts created automatically', line: 'Every new name on a call or thread becomes a contact on the right account, deduped and enriched.', to: '/product/account-intelligence' },
      { icon: Network, label: 'Org chart, built as you sell', line: 'Reporting lines, champions, blockers and the economic buyer, mapped from real conversations rather than a static import.', to: '/product/account-intelligence' },
      { icon: ClipboardCheck, label: 'Field updates on approval', line: 'Stage, amount, close date and your custom fields drafted for the rep. One tap to accept, or fully automatic once you trust it.', to: '/product/crm-auto-sync' },
      { icon: MessageCircle, label: 'One bot on WhatsApp, Botim, Slack & Teams', line: '"Sent the proposal to Ravi, they want it by Friday." Message the bot from any platform in plain language and it writes the update straight into the CRM.', to: '/product/crm-auto-sync', badge: 'New' },
      { icon: Mail, label: 'Emails with the full context', line: 'Follow-ups drafted from what was actually said on the call, addressed to the right person on the thread, not a generic template.', to: '/product/action-prescriptions' },
      { icon: Snowflake, label: 'Cold deal tagging', line: 'Deals that look alive on the board but have gone silent, flagged and dated before they wreck the forecast.', to: '/plugins' },
    ],
  },
  {
    id: 'walk', badge: 'Stage 2', name: 'Walk',
    promise: 'Learn why you win and why you lose',
    timeline: 'Weeks 3 to 6',
    framing: 'Now we point the models at your own history. Every closed deal you have ever run becomes the training set for the ones still open.',
    outcome: 'A forecast you can defend in a board meeting, and a written reason for every deal that is at risk.',
    workflows: [
      { icon: History, label: 'Historical deal pattern mining', line: 'We read your closed-won and closed-lost deals and extract what actually decided them, not what the notes claimed.', to: '/product/opportunities-pipeline' },
      { icon: FlaskConical, label: 'Win and loss ingredients', line: 'The traits your winning deals share, and the ones that show up right before you lose. Named, ranked, specific to your motion.', to: '/product/opportunities-pipeline' },
      { icon: Radar, label: 'Every open deal scored', line: 'Each live deal is matched against those traits and scored for conversion probability, not stage age.', to: '/product/risk-radar' },
      { icon: AlertTriangle, label: 'This deal will be lost, and why', line: 'The missing trait is called out by name. Single-threaded, no economic buyer engaged, price raised too early, champion gone quiet.', to: '/product/deal-risk-signals' },
      { icon: ArrowLeftRight, label: 'What changed in the forecast', line: 'Week on week movement with the exact event behind each swing, so the review starts with the delta instead of a spreadsheet.', to: '/product/pipeline-health-scoring' },
      { icon: Route, label: 'The move that unsticks it', line: 'Given the pattern, the single action most likely to move this deal forward, with the reasoning shown.', to: '/product/action-prescriptions' },
    ],
  },
  {
    id: 'run', badge: 'Stage 3', name: 'Run',
    promise: 'Execute the next move for every deal',
    timeline: 'Week 7 onwards',
    framing: 'Deals do not slip because leaders lacked data. They slip because nobody followed through. Run is where Forsyt stops reporting and starts executing.',
    outcome: 'An execution layer, not another reporting layer. Fewer admin hours, more selling hours, and a measurable ROI you can put in front of your CFO.',
    workflows: [
      { icon: Target, label: "Today's focus", line: 'One card per rep per day. The deal that matters most, the real blocker, the exact next step.', to: '/product/todays-focus' },
      { icon: Wand2, label: 'Do it for me', line: 'The follow-up, the proposal nudge, the multi-thread intro. Drafted, sent on approval, logged back automatically.', to: '/product/action-prescriptions' },
      { icon: Bell, label: 'Nudges where your team works', line: 'Risk fires into Slack, WhatsApp and email. Reps act on it without opening another tool.', to: '/product/smart-notifications', badge: 'New' },
      { icon: Users, label: 'Manager coaching plays', line: "When a pattern repeats across one rep's deals, the manager gets the play, not just the dashboard.", to: '/product/teams-overview' },
      { icon: CheckSquare, label: 'Tasks written back to the CRM', line: 'Every action closes the loop in Salesforce or Zoho, so the record never drifts back out of date.', to: '/product/crm-auto-sync' },
      { icon: Puzzle, label: 'Custom plugins for your motion', line: 'Anything specific to how you sell gets scoped as a PRD and built with you on the Forsyt store.', to: '/plugins' },
    ],
  },
];

const Panel = ({ stage, id, labelledBy }) => (
  <div id={id} role="tabpanel" aria-labelledby={labelledBy} className="animate-hfri-fade">
    <p className="text-base text-gray-600 leading-relaxed max-w-4xl">{stage.framing}</p>
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {stage.workflows.map((w, i) => {
        const Icon = w.icon;
        return (
          <Link
            key={i}
            to={w.to}
            data-testid={`hfri-workflow-${stage.id}-${i}`}
            className="group flex items-start gap-4 p-4 rounded-xl bg-white border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all duration-300"
          >
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
              <Icon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors duration-300" strokeWidth={1.9} />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">{w.label}</span>
                {w.badge && <span className="text-[9px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 rounded-full px-1.5 py-0.5">{w.badge}</span>}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mt-1">{w.line}</p>
            </div>
          </Link>
        );
      })}
    </div>
    <div className="mt-6 pt-5 border-t border-gray-100">
      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">What you have at the end</div>
      <p className="text-base font-semibold text-gray-900 leading-relaxed max-w-3xl">{stage.outcome}</p>
    </div>
  </div>
);

const HowForsytRollsIn = () => {
  const [active, setActive] = useState('crawl');
  const btnRefs = useRef([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const h = (window.location.hash || '').replace('#', '');
    if (STAGES.some((s) => s.id === h)) {
      setActive(h);
      requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, []);

  const activeIndex = STAGES.findIndex((s) => s.id === active);
  const activeStage = STAGES[activeIndex];

  const onKeyDown = (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
    const next = (activeIndex + dir + STAGES.length) % STAGES.length;
    setActive(STAGES[next].id);
    btnRefs.current[next]?.focus();
  };

  const select = (id) => {
    setActive(id);
    if (window.history?.replaceState) window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <section id="crawl" ref={sectionRef} className="py-24 bg-gradient-to-b from-gray-50 to-white scroll-mt-24" data-testid="how-forsyt-rolls-in">
      <style>{`@keyframes hfri-fade{from{opacity:0}to{opacity:1}}.animate-hfri-fade{animation:hfri-fade .15s ease-out}`}</style>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">How Forsyt rolls in</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
            Crawl. Walk. <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Run.</span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Capture the truth. Learn the pattern. Act on it. Each stage ships something usable on its own.</p>
        </div>

        {/* Card row */}
        <div role="tablist" aria-label="How Forsyt rolls in" onKeyDown={onKeyDown} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STAGES.map((s, i) => {
            const on = s.id === active;
            const Chevron = on ? ChevronUp : ChevronDown;
            return (
              <React.Fragment key={s.id}>
                <button
                  ref={(el) => (btnRefs.current[i] = el)}
                  role="tab"
                  id={`hfri-tab-${s.id}`}
                  aria-selected={on}
                  aria-expanded={on}
                  aria-controls={`hfri-panel-${s.id}`}
                  tabIndex={on ? 0 : -1}
                  onClick={() => select(s.id)}
                  data-testid={`hfri-card-${s.id}`}
                  className={`text-left rounded-xl p-5 transition-all duration-300 ${on ? 'bg-white border-2 border-blue-600 shadow-md' : 'bg-white border border-gray-200 hover:border-blue-200 hover:shadow-sm'}`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`text-xs font-bold uppercase tracking-wider ${on ? 'text-blue-600' : 'text-gray-400'}`}>{s.badge}</span>
                    <Chevron className={`w-4 h-4 ${on ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="mt-3 text-2xl font-bold text-gray-900">{s.name}</div>
                  <div className="mt-1 text-sm text-gray-600">{s.promise}</div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                    <Clock className="w-3.5 h-3.5" /> {s.timeline}
                  </div>
                </button>

                {/* Mobile accordion panel */}
                {on && (
                  <div className="md:hidden mt-1 mb-2 rounded-xl bg-white border border-gray-200 p-5" data-testid={`hfri-panel-mobile-${s.id}`}>
                    <Panel stage={s} id={`hfri-panel-${s.id}`} labelledBy={`hfri-tab-${s.id}`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Shared desktop panel */}
        <div className="hidden md:block mt-6 rounded-2xl bg-white border border-gray-200 shadow-sm p-8">
          <Panel key={active} stage={activeStage} id={`hfri-panel-${active}`} labelledBy={`hfri-tab-${active}`} />
        </div>

        {/* Footer CTA */}
        <div className="mt-10 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <p className="text-base text-blue-50 leading-relaxed max-w-2xl">
            <strong className="text-white">Most teams reach Run inside 45 days.</strong> We stand it up on your own pipeline first and prove ROI on your numbers before the commercial conversation.
          </p>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <a href="https://calendar.app.google/nswB9pt6ALLZ8xoy7" target="_blank" rel="noreferrer" data-testid="hfri-book" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-blue-700 text-sm font-bold hover:bg-blue-50 transition-colors">
              Book your Revenue OS briefing <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/product" data-testid="hfri-product" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/40 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
              Explore the product
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowForsytRollsIn;
